import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDateFnsLocale } from '../hooks/useDateFnsLocale';
import { format } from 'date-fns';
import { CalendarX, Pencil, Trash2, RotateCcw, ScrollText, EyeOff, Plus, ChevronDown, ChevronRight, Clock, AlertCircle, Download, BarChart3 } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useOrganizationStore } from '../stores/organizationStore';
import { Button, Input, Loader, Modal, ErrorDisplay, showToast } from '../components/common';
import { AttendanceSummaryModal } from '../components/statistics';
import { Header } from '../components/layout/Header';
import { api } from '../services/api';
import { timeEntryApi } from '../services/api';
import type { TimeEntry } from '../types';
import { parseWorkShifts, expectedMinutesForDate, formatDurationFromMinutes, type WorkShift } from '../utils/attendance';

interface UserAttendanceGroup {
  userId: string;
  firstName: string;
  lastName: string;
  email?: string;
  entries: TimeEntry[];
  totalMinutes: number;
  expectedMinutes: number;
  overtimeMinutes: number;
  workShifts: WorkShift[];
  workScheduleDays: number[];
  timezone: string;
}

interface ModifyLog {
  id: string;
  adminId: string;
  adminName: string;
  action: string;
  entryId: string | null;
  targetUserId: string;
  targetUserName: string;
  date: string;
  changes: Record<string, unknown> | null;
  reason: string | null;
  createdAt: string;
}

const toDateTimeLocal = (iso: string): string => {
  const d = new Date(iso);
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
};

const fromDateTimeLocal = (local: string): string => {
  return new Date(local).toISOString();
};

const STEALTH_PARAM = 'stealth=true';

export const AttendancePage: React.FC = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateFnsLocale();
  const { user } = useAuthStore();
  const { activeOrganizationId } = useOrganizationStore();
  const isAdmin = user?.organizationRoles?.find(r => r.orgId === activeOrganizationId)?.role === 'ADMIN';

  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd', { locale: dateFnsLocale }));
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState<TimeEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedNotesEntry, setSelectedNotesEntry] = useState<TimeEntry | null>(null);
  const [tick, setTick] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [editForm, setEditForm] = useState({
    checkIn: '',
    checkOut: '',
    checkInMethod: 'WEB',
    notes: '',
    checkOutNotes: '',
    reason: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    userId: '',
    checkIn: '',
    checkOut: '',
    checkInMethod: 'WEB',
    notes: '',
    checkOutNotes: '',
    reason: '',
  });
  const [deleteReason, setDeleteReason] = useState('');
  const [orgUsers, setOrgUsers] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState<ModifyLog[]>([]);
  // Work schedule returned by getMyAttendance (non-admin). Used as the
  // primary source for shifts/days/timezone, falling back to authStore.user.
  const [backendWorkSchedule, setBackendWorkSchedule] = useState<{
    workShifts: unknown;
    workScheduleDays: number[];
    timezone: string;
  } | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsPage, setLogsPage] = useState(1);

  // Export XLSX state
  const [showExport, setShowExport] = useState(false);
  const [exportMonth, setExportMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  // Optional export field selection (default: include all)
  const [exportIncludeCheckInNotes, setExportIncludeCheckInNotes] = useState(true);
  const [exportIncludeCheckOutNotes, setExportIncludeCheckOutNotes] = useState(true);
  const [exportIncludeLogs, setExportIncludeLogs] = useState(true);

  // Summary modal state
  const [showSummary, setShowSummary] = useState(false);

  const groupedRecords: UserAttendanceGroup[] = useMemo(() => {
    const groupsMap = new Map<string, UserAttendanceGroup>();

    for (const entry of records) {
      const userId = isAdmin ? (entry.user?.id ?? entry.userId) : (user?.id ?? entry.userId);
      if (!userId) continue;

      const firstName = isAdmin ? (entry.user?.firstName ?? '') : (user?.firstName ?? '');
      const lastName = isAdmin ? (entry.user?.lastName ?? '') : (user?.lastName ?? '');
      const email = isAdmin ? entry.user?.email : user?.email;
      const workShiftsRaw = isAdmin
        ? entry.user?.workShifts
        : (backendWorkSchedule?.workShifts ?? null);
      const workShifts = parseWorkShifts(workShiftsRaw);
      const workScheduleDays = isAdmin
        ? (entry.user?.workScheduleDays ?? [1,2,3,4,5])
        : (backendWorkSchedule?.workScheduleDays ?? user?.workScheduleDays ?? [1,2,3,4,5]);
      const timezone = isAdmin
        ? (entry.user?.timezone ?? 'UTC')
        : (backendWorkSchedule?.timezone ?? user?.timezone ?? 'UTC');

      if (!groupsMap.has(userId)) {
        groupsMap.set(userId, {
          userId,
          firstName,
          lastName,
          email,
          entries: [],
          totalMinutes: 0,
          expectedMinutes: 0,
          overtimeMinutes: 0,
          workShifts,
          workScheduleDays,
          timezone,
        });
      }

      const group = groupsMap.get(userId)!;
      group.entries.push(entry);
      // Calculate duration: use real-time for active entries (no checkOut), static for closed ones
      if (!entry.checkOut) {
        const now = new Date();
        const duration = Math.max(0, Math.floor((now.getTime() - new Date(entry.checkIn).getTime()) / 60000));
        group.totalMinutes += duration;
      } else {
        const duration = entry.durationMinutes ?? 0;
        group.totalMinutes += duration;
      }
    }

    // Calculate expected and overtime per group
    for (const group of groupsMap.values()) {
      group.expectedMinutes = expectedMinutesForDate(selectedDate, group.workShifts, group.workScheduleDays, group.timezone);
      group.overtimeMinutes = Math.max(0, group.totalMinutes - group.expectedMinutes);
      // Sort entries by checkIn ascending for display
      group.entries.sort((a, b) => new Date(a.checkIn).getTime() - new Date(b.checkIn).getTime());
    }

    return Array.from(groupsMap.values());
  }, [records, isAdmin, user, selectedDate, tick, backendWorkSchedule]);

  const toggleUserExpansion = (userId: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const isStealthMode = useMemo(() => {
    const search = window.location.search;
    const path = window.location.pathname;
    return path.includes('/modify') || new URLSearchParams(search).has('modify') || new URLSearchParams(search).get('stealth') === 'true';
  }, []);

  const stealthSuffix = isStealthMode ? '?' + STEALTH_PARAM : '';

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(tick => tick + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadAttendance();
  }, [selectedDate, page]);

  const loadAttendance = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const endpoint = isAdmin ? '/time-entries/admin/attendance' : '/time-entries/me/attendance';
      const params: Record<string, unknown> = { date: selectedDate, page, limit: 20 };
      const response = await api.get(endpoint, { params });
      if (response.data.success) {
        setRecords(response.data.data.entries || []);
        setTotalPages(response.data.data.pagination?.totalPages || 1);
        // Store work schedule from backend (non-admin endpoint includes it)
        if (!isAdmin && response.data.data.userWorkSchedule) {
          setBackendWorkSchedule(response.data.data.userWorkSchedule);
        } else {
          setBackendWorkSchedule(null);
        }
      }
    } catch (error) {
      console.error('Error loading attendance:', error);
      setLoadError(t('attendances.error.failedToLoad'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadLogs = async (lPage: number = 1) => {
    setLogsLoading(true);
    try {
      const params: Record<string, unknown> = { date: selectedDate, page: lPage, limit: 20 };
      const endpoint = isAdmin ? '/time-entries/admin/attendance-logs' : '/time-entries/me/attendance-logs';
      const response = await api.get(endpoint, { params });
      if (response.data.success) {
        setLogs(response.data.data.logs || []);
        setLogsTotalPages(response.data.data.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      showToast(t('attendances.errorLoadingLogs'), 'error');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleEditClick = (entry: TimeEntry) => {
    setEditingEntry(entry);
    setEditForm({
      checkIn: toDateTimeLocal(entry.checkIn),
      checkOut: entry.checkOut ? toDateTimeLocal(entry.checkOut) : '',
      checkInMethod: entry.checkInMethod || 'WEB',
      notes: entry.notes || '',
      checkOutNotes: entry.checkOutNotes || '',
      reason: '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    setIsSaving(true);
    try {
      const data: Record<string, unknown> = {
        checkIn: fromDateTimeLocal(editForm.checkIn),
        checkInMethod: editForm.checkInMethod,
        notes: editForm.notes || null,
        checkOutNotes: editForm.checkOutNotes || null,
        reason: editForm.reason,
      };
      if (editForm.checkOut) {
        data.checkOut = fromDateTimeLocal(editForm.checkOut);
      } else {
        data.checkOut = null;
      }
      await api.put(`/time-entries/admin/attendance/${editingEntry.id}${stealthSuffix}`, data);
      showToast(t('attendances.entryUpdated'), 'success');
      setEditingEntry(null);
      await loadAttendance();
    } catch (error) {
      console.error('Error updating entry:', error);
      showToast(t('attendances.errorUpdating'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingEntryId) return;
    try {
      const deletePayload: Record<string, unknown> = isStealthMode ? {} : { reason: deleteReason };
      await api.delete(`/time-entries/admin/attendance/${deletingEntryId}${stealthSuffix}`, { data: deletePayload });
      showToast(t('attendances.entryDeleted'), 'success');
      setDeletingEntryId(null);
      setDeleteReason('');
      await loadAttendance();
    } catch (error) {
      console.error('Error deleting entry:', error);
      showToast(t('attendances.errorDeleting'), 'error');
    }
  };

  const handleRestore = async (logId: string) => {
    try {
      await api.post(`/time-entries/admin/attendance-logs/${logId}/restore`);
      showToast(t('attendances.entryRestored'), 'success');
      await loadLogs(logsPage);
      await loadAttendance();
    } catch (error) {
      console.error('Error restoring entry:', error);
      showToast(t('attendances.errorRestoring'), 'error');
    }
  };

  const openLogs = () => {
    setShowLogs(true);
    setLogsPage(1);
    loadLogs(1);
  };

  const fetchOrgUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const response = await api.get('/users/list/names');
      if (response.data.success) {
        setOrgUsers(response.data.data || []);
      } else {
        setUsersError(t('attendances.failedToLoadUsers'));
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsersError(t('attendances.failedToLoadUsers'));
    } finally {
      setUsersLoading(false);
    }
  };

  const openCreateModal = () => {
    setCreateForm({
      userId: '',
      checkIn: '',
      checkOut: '',
      checkInMethod: 'WEB',
      notes: '',
      checkOutNotes: '',
      reason: '',
    });
    setShowCreate(true);
    fetchOrgUsers();
  };

  const openExportModal = () => {
    setExportMonth(format(new Date(), 'yyyy-MM'));
    setSelectedUserIds(new Set());
    setShowExport(true);
    if (isAdmin) {
      fetchOrgUsers();
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleSelectAllUsers = () => {
    setSelectedUserIds(prev => {
      if (prev.size === orgUsers.length) {
        return new Set();
      }
      return new Set(orgUsers.map(u => u.id));
    });
  };

  const handleExport = async () => {
    if (!exportMonth) {
      showToast(t('attendances.exportSelectMonth'), 'error');
      return;
    }
    setIsExporting(true);
    try {
      const params: {
        month: string;
        userIds?: string[];
        includeCheckInNotes?: boolean;
        includeCheckOutNotes?: boolean;
        includeLogs?: boolean;
      } = { month: exportMonth };
      // Admin may leave all users unselected to export the whole organization
      if (isAdmin && selectedUserIds.size > 0) {
        params.userIds = Array.from(selectedUserIds);
      }
      params.includeCheckInNotes = exportIncludeCheckInNotes;
      params.includeCheckOutNotes = exportIncludeCheckOutNotes;
      params.includeLogs = exportIncludeLogs;
      const response = await timeEntryApi.exportAttendanceXlsx(params);
      // Create blob download
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_${exportMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast(t('attendances.exportSuccess'), 'success');
      setShowExport(false);
    } catch (error: any) {
      console.error('Error exporting attendance:', error);
      let message = t('attendances.exportError');
      const errorData = error?.response?.data;
      if (errorData instanceof Blob) {
        try {
          const text = await errorData.text();
          const parsed = JSON.parse(text);
          if (parsed?.error?.message) {
            message = parsed.error.message;
          }
        } catch {
          // fall back to generic message
        }
      } else if (error?.response?.data?.error?.message) {
        message = error.response.data.error.message;
      }
      showToast(message, 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveCreate = async () => {
    if (!createForm.userId || !createForm.checkIn) return;
    setIsCreating(true);
    try {
      const data: Record<string, unknown> = {
        userId: createForm.userId,
        checkIn: fromDateTimeLocal(createForm.checkIn),
        checkInMethod: createForm.checkInMethod,
        reason: createForm.reason,
      };
      if (createForm.notes) data.notes = createForm.notes;
      if (createForm.checkOutNotes) data.checkOutNotes = createForm.checkOutNotes;
      if (createForm.checkOut) {
        data.checkOut = fromDateTimeLocal(createForm.checkOut);
      }
      await api.post('/time-entries/admin/attendance' + stealthSuffix, data);
      showToast(t('attendances.entryCreated'), 'success');
      setShowCreate(false);
      await loadAttendance();
    } catch (error) {
      console.error('Error creating entry:', error);
      showToast(t('attendances.errorCreating'), 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const formatDuration = (minutes: number | null | undefined, checkIn: Date | string, checkOut: Date | string | null) => {
    if (minutes !== null && minutes !== undefined) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    }
    if (!checkOut) {
      const start = new Date(checkIn);
      const now = new Date();
      const durationMins = Math.max(0, Math.floor((now.getTime() - start.getTime()) / 60000));
      const hours = Math.floor(durationMins / 60);
      const mins = durationMins % 60;
      return hours > 0 ? `${hours}h ${mins}m (${t('attendances.active')})` : `${mins}m (${t('attendances.active')})`;
    }
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const durationMins = Math.floor((end.getTime() - start.getTime()) / 60000);
    const hours = Math.floor(durationMins / 60);
    const mins = durationMins % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const openMaps = (lat: number, lon: number) => {
    window.open(`https://www.google.com/maps?q=${lat},${lon}`, '_blank');
  };

  const actionBadgeClass = (action: string) => {
    if (action === 'CREATE') return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-800/40';
    if (action === 'UPDATE') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40';
    if (action === 'DELETE') return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border border-red-200 dark:border-red-800/40';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  };

  const formatLogChanges = (log: ModifyLog): string => {
    if (!log.changes) return '-';
    const changes = log.changes as Record<string, unknown>;
    if (log.action === 'UPDATE') {
      const before = changes.before as Record<string, unknown> | undefined;
      const after = changes.after as Record<string, unknown> | undefined;
      const parts: string[] = [];
      if (before && after) {
        if (before.checkIn !== after.checkIn) {
          parts.push(t('attendances.checkInChanged'));
        }
        if (before.checkOut !== after.checkOut) {
          parts.push(t('attendances.checkOutChanged'));
        }
        if (before.notes !== after.notes) {
          parts.push(t('attendances.notesChanged'));
        }
        if (before.checkOutNotes !== after.checkOutNotes) {
          parts.push(t('attendances.checkOutNotesChanged'));
        }
      }
      return parts.length > 0 ? parts.join(', ') : t('attendances.noFieldChanges');
    }
    if (log.action === 'DELETE' || log.action === 'CREATE') {
      const checkIn = changes.checkIn ? new Date(changes.checkIn as string).toLocaleString() : '-';
      const checkOut = changes.checkOut ? new Date(changes.checkOut as string).toLocaleString() : 'active';
      return `check-in: ${checkIn}, check-out: ${checkOut}`;
    }
    return JSON.stringify(changes).slice(0, 100);
  };

  const pageTitle = isAdmin ? t('attendances.adminTitle') : t('time-attendance.ui.myAttendance');
  const pageSubtitle = isAdmin
    ? t('time-attendance.ui.viewAllCheckinsAndCheckouts')
    : t('time-attendance.ui.yourCheckinAndCheckoutHistory');

  return (
    <div className="flex flex-col h-full">
      <Header title={pageTitle} />

      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Page header with export action */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <p className="text-gray-500 dark:text-gray-400">{pageSubtitle}</p>
          <div className="flex flex-col sm:flex-row gap-2 self-end sm:self-auto">
            <Button
              variant="secondary"
              onClick={() => setShowSummary(true)}
              className="min-h-[44px] w-full sm:w-auto flex items-center justify-center gap-2"
            >
              <BarChart3 className="w-4 h-4" />
              {t('attendances.summaryButton')}
            </Button>
            <Button
              variant="secondary"
              onClick={openExportModal}
              className="min-h-[44px] w-full sm:w-auto flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              {t('attendances.export')}
            </Button>
          </div>
        </div>

        {/* Date Picker */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-end">
            <div className="w-full sm:w-auto sm:min-w-[200px]">
              <Input
                label={t('attendances.date')}
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Button
              variant="secondary"
              onClick={() => setSelectedDate(format(new Date(), 'yyyy-MM-dd', { locale: dateFnsLocale }))}
              className="min-h-[44px] w-full sm:w-auto"
            >
              {t('attendances.today')}
            </Button>
            <Button
              variant="secondary"
              onClick={openLogs}
              className="min-h-[44px] w-full sm:w-auto flex items-center gap-2"
            >
              <ScrollText className="w-4 h-4" />
              {t('attendances.logs')}
            </Button>
            {isAdmin && (
              <Button
                variant="primary"
                onClick={openCreateModal}
                className="min-h-[44px] w-full sm:w-auto flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t('attendances.add')}
              </Button>
            )}
            {isAdmin && isStealthMode && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40">
                <EyeOff className="w-4 h-4 text-red-600 dark:text-red-400" />
                <span className="text-sm font-medium text-red-600 dark:text-red-400">{t('attendances.stealthMode')}</span>
              </div>
            )}
          </div>
        </div>

        {loadError && (
          <div className="mb-4">
            <ErrorDisplay message={loadError} variant="error" dismissible onDismiss={() => setLoadError(null)} />
            <Button variant="secondary" onClick={loadAttendance} className="mt-2">{t('attendances.retry')}</Button>
          </div>
        )}

        {/* Attendance Groups */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CalendarX className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
            <div className="text-gray-900 dark:text-gray-100 font-medium mb-2">
              {t('attendances.emptyTitle')}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('attendances.emptyDescription')}
            </p>
          </div>
        ) : !isAdmin ? (
          /* Non-admin: flat table with total, no collapsible cards */
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            {/* Total summary */}
            <div className="flex items-center gap-2 px-4 sm:px-6 py-3 bg-gray-50 dark:bg-gray-900/50 text-sm border-b border-gray-200 dark:border-gray-700">
              <Clock className={`w-4 h-4 ${groupedRecords[0]?.entries.some(e => !e.checkOut) ? 'text-green-500' : 'text-gray-400'}`} />
              <span className="text-gray-500 dark:text-gray-400">{t('attendances.total')}:</span>
              <span className={`font-medium ${groupedRecords[0]?.entries.some(e => !e.checkOut) ? 'text-green-500' : 'text-gray-900 dark:text-white'}`}>{formatDurationFromMinutes(groupedRecords[0]?.totalMinutes ?? 0)}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('attendances.checkIn')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('attendances.checkOut')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('attendances.duration')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('attendances.method')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('attendances.location')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('attendances.observations')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {records.map((entry: TimeEntry) => (
                    <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {format(new Date(entry.checkIn), 'HH:mm', { locale: dateFnsLocale })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {entry.checkOut
                          ? format(new Date(entry.checkOut), 'HH:mm', { locale: dateFnsLocale })
                          : <span className="text-green-500 font-medium">{t('attendances.active')}</span>
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDuration(entry.durationMinutes, entry.checkIn, entry.checkOut ?? null)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {entry.checkInMethod === 'Kiosk Web App' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/40">
                            {'📱 ' + t('attendances.kioskWebApp')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                            {'💻 ' + t('attendances.webPortal')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {entry.latitude && entry.longitude ? (
                          <button
                            onClick={() => openMaps(entry.latitude!, entry.longitude!)}
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium min-h-[44px] min-w-[44px] flex items-center justify-center"
                          >
                            {'📍 ' + t('attendances.view')}
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 max-w-[200px] truncate">
                        {(entry.notes || entry.checkOutNotes) ? (
                          <button
                            onClick={() => setSelectedNotesEntry(entry)}
                            className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                          >
                            {t('attendances.viewNotes')}
                          </button>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Admin: collapsible cards grouped by user */
          <div className="space-y-4">
            {groupedRecords.map((group) => {
              const isExpanded = expandedUsers.has(group.userId);
              return (
                <div key={group.userId} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  {/* Header row - clickable */}
                  <button
                    onClick={() => toggleUserExpansion(group.userId)}
                    className="w-full flex items-center justify-between px-4 sm:px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                      <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                        <span className="text-primary-600 dark:text-primary-400 text-sm font-medium">
                          {group.firstName?.[0]}{group.lastName?.[0]}
                        </span>
                      </div>
                      <div className="text-left">
                        <p className={`text-sm font-medium ${group.entries.some(e => !e.checkOut) ? 'text-green-500' : 'text-gray-900 dark:text-white'}`}>
                          {group.firstName} {group.lastName}
                        </p>
                        {group.email && (
                          <p className="text-xs text-gray-500">{group.email}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className={`w-4 h-4 ${group.entries.some(e => !e.checkOut) ? 'text-green-500' : 'text-gray-400'}`} />
                        <span className={`font-medium ${group.entries.some(e => !e.checkOut) ? 'text-green-500' : 'text-gray-900 dark:text-white'}`}>{formatDurationFromMinutes(group.totalMinutes)}</span>
                      </div>
                      <span className="text-xs text-gray-400">{group.entries.length} {t('attendances.entries')}</span>
                    </div>
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 dark:border-gray-700">
                      {/* Summary: total, expected, overtime */}
                      <div className="flex flex-wrap items-center gap-4 px-4 sm:px-6 py-3 bg-gray-50 dark:bg-gray-900/50 text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-500 dark:text-gray-400">{t('attendances.total')}:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{formatDurationFromMinutes(group.totalMinutes)}</span>
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500 dark:text-gray-400">{t('attendances.expected')}:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatDurationFromMinutes(group.expectedMinutes)}</span>
                          </div>
                        )}
                        {isAdmin && (
                          group.overtimeMinutes > 0 ? (
                            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 border border-orange-200 dark:border-orange-800/40">
                              <AlertCircle className="w-4 h-4" />
                              <span className="font-medium">{t('attendances.overtime')}: {formatDurationFromMinutes(group.overtimeMinutes)}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-800/40">
                              <span className="font-medium">{t('attendances.noOvertime')}</span>
                            </div>
                          )
                        )}
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[600px] divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t('attendances.checkIn')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t('attendances.checkOut')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t('attendances.duration')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t('attendances.method')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t('attendances.location')}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t('attendances.observations')}
                              </th>
                              {isAdmin && (
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  {t('attendances.actions')}
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {group.entries.map((entry: TimeEntry) => (
                              <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {format(new Date(entry.checkIn), 'HH:mm', { locale: dateFnsLocale })}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {entry.checkOut
                                    ? format(new Date(entry.checkOut), 'HH:mm', { locale: dateFnsLocale })
                                    : <span className="text-green-500 font-medium">{t('attendances.active')}</span>
                                  }
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {formatDuration(entry.durationMinutes, entry.checkIn, entry.checkOut ?? null)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {entry.checkInMethod === 'Kiosk Web App' ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/40">
                                      {'📱 ' + t('attendances.kioskWebApp')}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                                      {'💻 ' + t('attendances.webPortal')}
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {entry.latitude && entry.longitude ? (
                                    <button
                                      onClick={() => openMaps(entry.latitude!, entry.longitude!)}
                                      className="text-primary-600 hover:text-primary-800 text-sm font-medium min-h-[44px] min-w-[44px] flex items-center justify-center"
                                    >
                                      {'📍 ' + t('attendances.view')}
                                    </button>
                                  ) : (
                                    <span className="text-gray-400 text-sm">-</span>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 max-w-[200px] truncate">
                                  {(entry.notes || entry.checkOutNotes) ? (
                                    <button
                                      onClick={() => setSelectedNotesEntry(entry)}
                                      className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                                    >
                                      {t('attendances.viewNotes')}
                                    </button>
                                  ) : (
                                    <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                                  )}
                                </td>
                                {isAdmin && (
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleEditClick(entry)}
                                        className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center"
                                        title={t('attendances.editEntry')}
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => setDeletingEntryId(entry.id)}
                                        className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center"
                                        title={t('attendances.deleteEntryTitle')}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {records.length > 0 && (
          <div className="flex justify-center items-center gap-4 mt-6">
            <Button
              variant="secondary"
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              {t('attendances.previous')}
            </Button>
            <span className="text-gray-600 dark:text-gray-400">
              {t('attendances.pageOf', { page, totalPages })}
            </span>
            <Button
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              {t('attendances.next')}
            </Button>
          </div>
        )}

        {/* Observations Modal */}
        <Modal
          isOpen={!!selectedNotesEntry}
          onClose={() => setSelectedNotesEntry(null)}
          title={t('time-attendance.ui.attendanceObservations')}
        >
          {selectedNotesEntry && (
            <div className="space-y-4">
              {selectedNotesEntry.notes && (
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('attendances.checkIn')}</h4>
                  <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap text-sm">{selectedNotesEntry.notes}</p>
                </div>
              )}
              {selectedNotesEntry.checkOutNotes && (
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('attendances.checkOut')}</h4>
                  <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap text-sm">{selectedNotesEntry.checkOutNotes}</p>
                </div>
              )}
              <div className="pt-4">
                <Button variant="secondary" onClick={() => setSelectedNotesEntry(null)} className="w-full">
                  {t('common.close')}
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={!!editingEntry}
          onClose={() => setEditingEntry(null)}
          title={t('attendances.editAttendance')}
          size="lg"
        >
          {editingEntry && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.checkInTime')}</label>
                <Input
                  type="datetime-local"
                  value={editForm.checkIn}
                  onChange={(e) => setEditForm({ ...editForm, checkIn: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.checkOutTime')}</label>
                <Input
                  type="datetime-local"
                  value={editForm.checkOut}
                  onChange={(e) => setEditForm({ ...editForm, checkOut: e.target.value })}
                />
                <p className="text-xs text-gray-400 mt-1">{t('attendances.checkOutOptional')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.checkInMethod')}</label>
                <select
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[44px]"
                  value={editForm.checkInMethod}
                  onChange={(e) => setEditForm({ ...editForm, checkInMethod: e.target.value })}
                >
                  <option value="WEB">{t('attendances.webPortal')}</option>
                  <option value="Kiosk Web App">{t('attendances.kioskWebApp')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.notes')}</label>
                <textarea
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[80px]"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder={t('attendances.notesPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.checkOutNotes')}</label>
                <textarea
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[80px]"
                  value={editForm.checkOutNotes}
                  onChange={(e) => setEditForm({ ...editForm, checkOutNotes: e.target.value })}
                  placeholder={t('attendances.checkOutNotesPlaceholder')}
                />
              </div>
              {!isStealthMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.reason')} <span className="text-red-500">*</span></label>
                  <textarea
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[80px]"
                    value={editForm.reason}
                    onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                    placeholder={t('attendances.reasonPlaceholder')}
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setEditForm({ ...editForm, reason: t('attendances.forgotCheckIn') })}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600"
                    >
                      {t('attendances.forgotCheckIn')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditForm({ ...editForm, reason: t('attendances.forgotCheckOut') })}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600"
                    >
                      {t('attendances.forgotCheckOut')}
                    </button>
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="primary"
                  onClick={handleSaveEdit}
                  disabled={isSaving || (!isStealthMode && !editForm.reason.trim())}
                  className="flex-1"
                >
                  {isSaving ? <Loader /> : t('attendances.saveChanges')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setEditingEntry(null)}
                  className="flex-1"
                >
                  {t('attendances.cancel')}
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Create Attendance Modal */}
        <Modal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          title={t('attendances.newAttendance')}
          size="lg"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.user')}</label>
              {usersLoading ? (
                <div className="flex justify-center py-3">
                  <Loader />
                </div>
              ) : usersError ? (
                <div className="space-y-2">
                  <ErrorDisplay message={usersError} variant="error" />
                  <Button variant="secondary" onClick={fetchOrgUsers} className="text-sm">
                    {t('attendances.retry')}
                  </Button>
                </div>
              ) : (
                <select
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[44px]"
                  value={createForm.userId}
                  onChange={(e) => setCreateForm({ ...createForm, userId: e.target.value })}
                >
                  <option value="">{t('attendances.selectUser')}</option>
                  {orgUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.checkInTime')}</label>
              <Input
                type="datetime-local"
                value={createForm.checkIn}
                onChange={(e) => setCreateForm({ ...createForm, checkIn: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.checkOutTime')}</label>
              <Input
                type="datetime-local"
                value={createForm.checkOut}
                onChange={(e) => setCreateForm({ ...createForm, checkOut: e.target.value })}
              />
              <p className="text-xs text-gray-400 mt-1">{t('attendances.checkOutOptional')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.checkInMethod')}</label>
              <select
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[44px]"
                value={createForm.checkInMethod}
                onChange={(e) => setCreateForm({ ...createForm, checkInMethod: e.target.value })}
              >
                <option value="WEB">Web Portal</option>
                <option value="Kiosk Web App">Kiosk Web App</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.notes')}</label>
              <textarea
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[80px]"
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                placeholder={t('attendances.notesPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.checkOutNotes')}</label>
              <textarea
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[80px]"
                value={createForm.checkOutNotes}
                onChange={(e) => setCreateForm({ ...createForm, checkOutNotes: e.target.value })}
                placeholder={t('attendances.checkOutNotesPlaceholder')}
              />
            </div>
            {!isStealthMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.reason')} <span className="text-red-500">*</span></label>
                <textarea
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[80px]"
                  value={createForm.reason}
                  onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })}
                  placeholder={t('attendances.reasonPlaceholder')}
                />
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setCreateForm({ ...createForm, reason: t('attendances.forgotCheckIn') })}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600"
                  >
                    {t('attendances.forgotCheckIn')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreateForm({ ...createForm, reason: t('attendances.forgotCheckOut') })}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600"
                  >
                    {t('attendances.forgotCheckOut')}
                  </button>
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button
                variant="primary"
                onClick={handleSaveCreate}
                disabled={isCreating || !createForm.userId || !createForm.checkIn || (!isStealthMode && !createForm.reason.trim())}
                className="flex-1"
              >
                {isCreating ? <Loader /> : t('attendances.createEntry')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowCreate(false)}
                className="flex-1"
              >
                {t('attendances.cancel')}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={!!deletingEntryId}
          onClose={() => setDeletingEntryId(null)}
          title={t('attendances.deleteEntry')}
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              {t('attendances.confirmDelete')}
              {isStealthMode && (
                <span className="block mt-2 text-sm text-red-600 dark:text-red-400">
                  {t('attendances.stealthWarning')}
                </span>
              )}
            </p>
            {!isStealthMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('attendances.reason')} <span className="text-red-500">*</span></label>
                <textarea
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white min-h-[80px]"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder={t('attendances.reasonPlaceholder')}
                />
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setDeleteReason(t('attendances.forgotCheckIn'))}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600"
                  >
                    {t('attendances.forgotCheckIn')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteReason(t('attendances.forgotCheckOut'))}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600"
                  >
                    {t('attendances.forgotCheckOut')}
                  </button>
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button
                variant="primary"
                onClick={confirmDelete}
                disabled={!isStealthMode && !deleteReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {t('attendances.delete')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => { setDeletingEntryId(null); setDeleteReason(''); }}
                className="flex-1"
              >
                {t('attendances.cancel')}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Logs Modal */}
        <Modal
          isOpen={showLogs}
          onClose={() => setShowLogs(false)}
          title={t('attendances.attendanceLogs')}
          size="xl"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('attendances.logs')}: {selectedDate}
              </p>
              <Button variant="secondary" onClick={() => loadLogs(logsPage)} className="text-sm">
                {t('attendances.refresh')}
              </Button>
            </div>

            {logsLoading ? (
              <div className="flex justify-center py-8">
                <Loader />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('attendances.noLogsFound')}
              </div>
            ) : (
              <div className="space-y-3">
                {logs.map((log: ModifyLog) => (
                  <div
                    key={log.id}
                    className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-100 dark:border-gray-700"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${actionBadgeClass(log.action)}`}
                          >
                            {log.action}
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {log.adminName}
                          </span>
                          <span className="text-xs text-gray-400">{'->'}</span>
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {log.targetUserName}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {new Date(log.createdAt).toLocaleString()}
                        </p>
                        {log.reason && (
                          <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">
                            <span className="font-medium">{t('attendances.reason')}:</span> {log.reason}
                          </p>
                        )}
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {formatLogChanges(log)}
                        </p>
                      </div>
                      {isAdmin && log.action === 'DELETE' && (
                        <button
                          onClick={() => handleRestore(log.id)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors whitespace-nowrap"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          {t('attendances.restore')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {logs.length > 0 && logsTotalPages > 1 && (
              <div className="flex justify-center items-center gap-4 pt-4">
                <Button
                  variant="secondary"
                  disabled={logsPage === 1}
                  onClick={() => {
                    const p = logsPage - 1;
                    setLogsPage(p);
                    loadLogs(p);
                  }}
                >
                  {t('attendances.previous')}
                </Button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('attendances.pageOf', { page: logsPage, totalPages: logsTotalPages })}
                </span>
                <Button
                  variant="secondary"
                  disabled={logsPage >= logsTotalPages}
                  onClick={() => {
                    const p = logsPage + 1;
                    setLogsPage(p);
                    loadLogs(p);
                  }}
                >
                  {t('attendances.next')}
                </Button>
              </div>
            )}

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="secondary" onClick={() => setShowLogs(false)} className="w-full">
                {t('common.close')}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Export CSV Modal */}
        <Modal
          isOpen={showExport}
          onClose={() => setShowExport(false)}
          title={t('attendances.exportTitle')}
          size="lg"
        >
          <div className="space-y-4">
            {/* Month Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('attendances.exportMonth')}
              </label>
              <Input
                type="month"
                value={exportMonth}
                onChange={(e) => setExportMonth(e.target.value)}
              />
            </div>

            {/* User selection (admin only) */}
            {isAdmin && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('attendances.exportSelectUsers')}
                  </label>
                  <button
                    type="button"
                    onClick={toggleSelectAllUsers}
                    className="text-xs font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400"
                  >
                    {selectedUserIds.size === orgUsers.length && orgUsers.length > 0
                      ? t('attendances.exportDeselectAll')
                      : t('attendances.exportSelectAll')}
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {t('attendances.exportAllUsersHint')}
                </p>
                {usersError ? (
                  <div className="rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 p-3">
                    <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                      {t('attendances.exportLoadUsersError')}
                    </p>
                    <button
                      type="button"
                      onClick={fetchOrgUsers}
                      className="text-xs font-medium text-red-700 dark:text-red-300 underline hover:no-underline"
                    >
                      {t('common.retry')}
                    </button>
                  </div>
                ) : usersLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader />
                  </div>
                ) : orgUsers.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('attendances.exportNoUsers')}
                  </p>
                ) : (
                  <div className="max-h-[300px] overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    {orgUsers.map((u) => (
                      <label
                        key={u.id}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer border-b border-gray-100 dark:border-gray-700/50 last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUserIds.has(u.id)}
                          onChange={() => toggleUserSelection(u.id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-900 dark:text-white">
                          {u.firstName} {u.lastName}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Optional export fields */}
            <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('attendances.exportOptionalFields')}
              </label>
              {[
                { checked: exportIncludeCheckInNotes, onChange: setExportIncludeCheckInNotes, label: 'attendances.exportIncludeCheckInNotes' },
                { checked: exportIncludeCheckOutNotes, onChange: setExportIncludeCheckOutNotes, label: 'attendances.exportIncludeCheckOutNotes' },
                { checked: exportIncludeLogs, onChange: setExportIncludeLogs, label: 'attendances.exportIncludeLogs' },
              ].map((option) => (
                <label key={option.label} className="flex items-center gap-3 px-1 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={option.checked}
                    onChange={(e) => option.onChange(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-900 dark:text-white">
                    {t(option.label)}
                  </span>
                </label>
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="primary"
                onClick={handleExport}
                disabled={isExporting || !exportMonth}
                className="flex-1 flex items-center justify-center gap-2"
              >
                {isExporting ? (
                  <>
                    <Loader />
                    {t('attendances.exportLoading')}
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    {t('attendances.export')}
                  </>
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowExport(false)}
                className="flex-1"
              >
                {t('common.close')}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Attendance Summary Modal */}
        <AttendanceSummaryModal
          isOpen={showSummary}
          onClose={() => setShowSummary(false)}
          isAdmin={isAdmin}
          selectedDate={selectedDate}
          backendWorkSchedule={backendWorkSchedule}
        />
      </main>
    </div>
  );
};

export default AttendancePage;
